import React, { useState } from "react";

export default function GeneralContactForm({ defaultService = "Sommerhusrengøring", className = "" }) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    serviceType: defaultService,
    bookingDay: "",
    bookingTime: "",
    bookingMonth: "",
    description: "",
    website_url: "" // Honeypot field
  });

  // Keep serviceType updated if defaultService prop changes
  React.useEffect(() => {
    if (defaultService) {
      setFormData((prev) => ({ ...prev, serviceType: defaultService }));
    }
  }, [defaultService]);

  const [status, setStatus] = useState({ loading: false, success: false, error: null });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Spam honeypot check
    if (formData.website_url) {
      // Bot filled out the invisible honeypot field
      setStatus({ loading: false, success: true, error: null });
      return;
    }

    if (!formData.name || !formData.phone || !formData.email || !formData.description) {
      setStatus({ loading: false, success: false, error: "Udfyld venligst alle påkrævede felter." });
      return;
    }

    setStatus({ loading: true, success: false, error: null });

    try {
      const response = await fetch("/_api/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          form_type: "Rengøringshenvendelse",
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          serviceType: formData.serviceType,
          bookingDay: formData.bookingDay,
          bookingTime: formData.bookingTime,
          bookingMonth: formData.bookingMonth,
          description: formData.description,
          website_url: formData.website_url
        })
      });

      if (!response.ok) {
        throw new Error("Fejl ved afsendelse. Prøv venligst igen eller ring direkte.");
      }

      setStatus({ loading: false, success: true, error: null });
      setFormData({
        name: "",
        phone: "",
        email: "",
        serviceType: defaultService,
        bookingDay: "",
        bookingTime: "",
        bookingMonth: "",
        description: "",
        website_url: ""
      });
    } catch (err) {
      setStatus({ loading: false, success: false, error: err.message });
    }
  };

  return (
    <div className={`bg-[#FFFDF9] rounded-2xl border border-[#EADFCF] p-6 sm:p-8 shadow-sm ${className}`}>
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#887055] mb-1">
          <i className="fa-solid fa-paper-plane" aria-hidden="true"></i>
          <span>Send uforpligtende forespørgsel</span>
        </div>
        <h3 className="text-2xl font-bold font-heading text-[#2C2523]">
          {formData.serviceType === "Tømning af dødsbo, derefter rengøring"
            ? "Bestil eller få tilbud på dødsbo & rengøring"
            : formData.serviceType === "Rengøring af hus eller lejlighed"
            ? "Bestil rengøring af hus eller lejlighed"
            : "Få et tilbud på rengøring"}
        </h3>
        <p className="text-sm text-[#5C524C] mt-1">
          {formData.serviceType === "Tømning af dødsbo, derefter rengøring"
            ? "Udfyld formularen, så vender vi hurtigt tilbage med en tryg og respektfuld dialog samt et uforpligtende tilbud."
            : formData.serviceType === "Rengøring af hus eller lejlighed"
            ? "Udfyld formularen, så kontakter vi dig hurtigt om dine ønsker til privat rengøring."
            : "Udfyld formularen, så vender vi hurtigt tilbage til dig vedrørende dit sommerhus eller feriebolig."}
        </p>
      </div>

      {/* Information om ferie / lukket */}
      <div className="mb-6 p-4 rounded-xl bg-[#FAF7F2] border border-[#EADFCF] text-[#4B3C2D] flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-[#EADFCF] flex items-center justify-center shrink-0 text-[#887055] mt-0.5">
          <i className="fa-regular fa-calendar-days text-sm" aria-hidden="true"></i>
        </div>
        <div className="text-xs sm:text-sm space-y-2">
          <div>
            <p className="font-bold text-[#2C2523] flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-600 inline-block"></span>
              Lukket for modtagelse af kunder i marts og start-april:
            </p>
            <p className="text-[#5C524C] mt-0.5 leading-relaxed">
              Der er <strong className="text-[#2C2523]">lukket for modtagelse af kunder i hele marts måned og til og med 4. april</strong>.
            </p>
          </div>
          <div className="pt-2 border-t border-[#EADFCF]/70">
            <p className="font-semibold text-[#2C2523]">
              Juleferie:
            </p>
            <p className="text-[#5C524C] mt-0.5">
              Vi holder juleferie fra <strong className="text-[#2C2523]">den 20. december til og med den 3. januar 2027</strong>. Du kan stadig sagtens indsende din bestilling eller forespørgsel her – vi vender tilbage hurtigst muligt.
            </p>
          </div>
        </div>
      </div>

      {status.success ? (
        <div className="bg-[#FAF7F2] border border-[#C2AB8E] rounded-xl p-6 text-center space-y-3 animate-in fade-in duration-300">
          <div className="w-12 h-12 rounded-full bg-[#EADFCF] text-[#6B5640] flex items-center justify-center mx-auto text-xl">
            <i className="fa-solid fa-check" aria-hidden="true"></i>
          </div>
          <h4 className="text-lg font-bold text-[#2C2523] font-heading">Tak for din forespørgsel!</h4>
          <p className="text-sm text-[#5C524C]">
            Vi har modtaget din besked og kontakter dig hurtigst muligt. Du er også altid velkommen til at ringe på{" "}
            <a href="tel:40947540" className="font-semibold underline text-[#2C2523] whitespace-nowrap">
              40 94 75 40
            </a>.
          </p>
          <button
            type="button"
            onClick={() => setStatus({ loading: false, success: false, error: null })}
            className="mt-2 text-xs font-semibold text-[#887055] hover:text-[#2C2523] underline cursor-pointer"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Honeypot anti-spam input */}
          <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Website URL (leave blank)</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {/* Punkter for bestilling af rengøring */}
          <div className="p-4 bg-[#F4EFE6] border border-[#EADFCF] rounded-xl text-xs text-[#2C2523] space-y-2">
            <div className="font-semibold text-xs text-[#6B5640] uppercase tracking-wider flex items-center gap-2">
              <i className="fa-solid fa-calendar-check text-[#887055]" aria-hidden="true"></i>
              <span>Bestilling af rengøring:</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
              <div className="flex items-center gap-2 bg-white/90 px-3 py-2 rounded-lg border border-[#EADFCF]">
                <i className="fa-solid fa-calendar-day text-[#887055] shrink-0" aria-hidden="true"></i>
                <span className="font-medium text-[#2C2523]">Mandag til fredag</span>
              </div>
              <div className="flex items-center gap-2 bg-white/90 px-3 py-2 rounded-lg border border-[#EADFCF]">
                <i className="fa-solid fa-clock text-[#887055] shrink-0" aria-hidden="true"></i>
                <span className="font-medium text-[#2C2523]">Mellem 9 og 15</span>
              </div>
              <div className="flex items-center gap-2 bg-white/90 px-3 py-2 rounded-lg border border-[#EADFCF]">
                <i className="fa-solid fa-calendar-days text-[#887055] shrink-0" aria-hidden="true"></i>
                <span className="font-medium text-[#2C2523]">Sep okt nov og dec</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="block text-xs font-semibold text-[#2C2523] mb-1">
                Dit Navn <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="F.eks. Mette Hansen"
                className="w-full px-4 py-2.5 rounded-xl border border-[#EADFCF] bg-[#FAF7F2] text-sm text-[#2C2523] placeholder-[#A88F71]/60 focus:outline-none focus:ring-2 focus:ring-[#887055] focus:bg-white transition-all"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-xs font-semibold text-[#2C2523] mb-1">
                Telefonnummer <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="F.eks. 40 94 75 40"
                className="w-full px-4 py-2.5 rounded-xl border border-[#EADFCF] bg-[#FAF7F2] text-sm text-[#2C2523] placeholder-[#A88F71]/60 focus:outline-none focus:ring-2 focus:ring-[#887055] focus:bg-white transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="email" className="block text-xs font-semibold text-[#2C2523] mb-1">
                E-mailadresse <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                className="w-full px-4 py-2.5 rounded-xl border border-[#EADFCF] bg-[#FAF7F2] text-sm text-[#2C2523] placeholder-[#A88F71]/60 focus:outline-none focus:ring-2 focus:ring-[#887055] focus:bg-white transition-all"
              />
            </div>

            <div>
              <label htmlFor="serviceType" className="block text-xs font-semibold text-[#2C2523] mb-1">
                Ydelse
              </label>
              <select
                id="serviceType"
                name="serviceType"
                value={formData.serviceType}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border border-[#EADFCF] bg-[#FAF7F2] text-sm text-[#2C2523] focus:outline-none focus:ring-2 focus:ring-[#887055] focus:bg-white transition-all cursor-pointer"
              >
                <option value="Sommerhusrengøring">Sommerhusrengøring</option>
                <option value="Airbnb-rengøring">Airbnb-rengøring</option>
                <option value="Tømning af dødsbo, derefter rengøring">Tømning af dødsbo, derefter rengøring</option>
                <option value="Rengøring af hus eller lejlighed">Rengøring af hus eller lejlighed</option>
                <option value="Begge dele / Generel rengøring">Flere ydelser / Anden rengøring</option>
              </select>
            </div>
          </div>

          {/* Valg af ønsket ugedag, tidsrum og måned */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label htmlFor="clean_booking_day" className="block text-xs font-semibold text-[#2C2523] mb-1">
                Ønsket ugedag
              </label>
              <select
                id="clean_booking_day"
                name="bookingDay"
                value={formData.bookingDay}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border border-[#EADFCF] bg-[#FAF7F2] text-sm text-[#2C2523] focus:outline-none focus:ring-2 focus:ring-[#887055] focus:bg-white transition-all cursor-pointer"
              >
                <option value="">Vælg ugedag (mandag til fredag)</option>
                <option value="Mandag">Mandag</option>
                <option value="Tirsdag">Tirsdag</option>
                <option value="Onsdag">Onsdag</option>
                <option value="Torsdag">Torsdag</option>
                <option value="Fredag">Fredag</option>
                <option value="Mandag til fredag (fleksibel)">Mandag til fredag (fleksibel)</option>
              </select>
            </div>

            <div>
              <label htmlFor="clean_booking_time" className="block text-xs font-semibold text-[#2C2523] mb-1">
                Ønsket tidsrum
              </label>
              <select
                id="clean_booking_time"
                name="bookingTime"
                value={formData.bookingTime}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border border-[#EADFCF] bg-[#FAF7F2] text-sm text-[#2C2523] focus:outline-none focus:ring-2 focus:ring-[#887055] focus:bg-white transition-all cursor-pointer"
              >
                <option value="">Vælg tidsrum (mellem 9 og 15)</option>
                <option value="Mellem 9 og 12">Mellem 9 og 12</option>
                <option value="Mellem 12 og 15">Mellem 12 og 15</option>
                <option value="Mellem 9 og 15 (fleksibel)">Mellem 9 og 15 (fleksibel)</option>
              </select>
            </div>

            <div>
              <label htmlFor="clean_booking_month" className="block text-xs font-semibold text-[#2C2523] mb-1">
                Ønsket måned
              </label>
              <select
                id="clean_booking_month"
                name="bookingMonth"
                value={formData.bookingMonth}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border border-[#EADFCF] bg-[#FAF7F2] text-sm text-[#2C2523] focus:outline-none focus:ring-2 focus:ring-[#887055] focus:bg-white transition-all cursor-pointer"
              >
                <option value="">Vælg måned (sep okt nov og dec)</option>
                <option value="September">September</option>
                <option value="Oktober">Oktober</option>
                <option value="November">November</option>
                <option value="December">December</option>
                <option value="Hurtigst muligt">Hurtigst muligt</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="description" className="block text-xs font-semibold text-[#2C2523] mb-1">
              Beskrivelse af opgaven <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              rows={4}
              required
              value={formData.description}
              onChange={handleChange}
              placeholder="Skriv om sommerhuset/Airbnb-boligens placering, størrelse eller specifikke ønsker..."
              className="w-full px-4 py-2.5 rounded-xl border border-[#EADFCF] bg-[#FAF7F2] text-sm text-[#2C2523] placeholder-[#A88F71]/60 focus:outline-none focus:ring-2 focus:ring-[#887055] focus:bg-white transition-all"
            />
          </div>

          {status.error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl flex items-center gap-2">
              <i className="fa-solid fa-circle-exclamation shrink-0" aria-hidden="true"></i>
              <span>{status.error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status.loading}
            className="w-full bg-[#3A302B] hover:bg-[#231C19] text-[#FFFDF9] py-3.5 px-6 rounded-xl font-semibold text-sm transition-all duration-200 shadow-sm flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {status.loading ? (
              <>
                <i className="fa-solid fa-spinner animate-spin text-sm" aria-hidden="true"></i>
                <span>Sender forespørgsel...</span>
              </>
            ) : (
              <>
                <span>Send forespørgsel</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
